import React, { useState, useContext, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { LanguageContext } from '../context/LanguageContext';
import emailjs from '@emailjs/browser';

const Contact = () => {
  const location = useLocation();
  const { t } = useContext(LanguageContext);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    object: location.state?.service ? t(location.state.service) : '',
    message: ''
  });
  const [status, setStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConsentChecked, setIsConsentChecked] = useState(false);
  const [errorDetails, setErrorDetails] = useState('');

  useEffect(() => {
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    
    console.log('EmailJS Configuration:', {
      publicKey,
      serviceId,
      templateId,
      hasPublicKey: !!publicKey,
      hasServiceId: !!serviceId,
      hasTemplateId: !!templateId
    });

    if (!publicKey || !serviceId || !templateId) {
      console.error('Configuration EmailJS manquante:', {
        publicKey: !!publicKey,
        serviceId: !!serviceId,
        templateId: !!templateId
      });
      return;
    }
    
    try {
      emailjs.init(publicKey);
      console.log('EmailJS initialisé avec succès');
    } catch (error) {
      console.error('Erreur lors de l\'initialisation d\'EmailJS:', error);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus('');
    setErrorDetails('');

    try {
      const templateParams = {
        from_name: formData.name,
        reply_to: formData.email,
        subject: formData.object,
        message: formData.message
      };

      console.log('Tentative d\'envoi avec:', {
        serviceId: process.env.REACT_APP_EMAILJS_SERVICE_ID,
        templateId: process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        params: templateParams
      });

      const response = await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        templateParams
      );

      console.log('Email envoyé avec succès:', response);
      setStatus('success');
      setFormData({ name: '', email: '', object: '', message: '' });
      setIsConsentChecked(false);
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
      setStatus('error');
      setErrorDetails(error.text || error.message || 'Erreur lors de l\'envoi du message');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    console.log(`Field ${e.target.name} changed to: ${e.target.value}`);
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-36 pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl font-bold text-indigo-900 mb-6">
            {t('contactTitle')}
          </h1>
          <p className="text-xl text-gray-600">
            {t('contactIntro')}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-xl p-8 md:p-12"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('fullName')}
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-600"
                  required
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('email')}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-600"
                  required
                />
              </div>
            </div>
            <div>
              <label htmlFor="object" className="block text-sm font-medium text-gray-700 mb-2">
                Objet {/* Changer de "object" à "Objet" */}
              </label>
              <input
                type="text"
                id="object"
                name="object"
                value={formData.object}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-600"
                required
              />
            </div>
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                {t('message')}
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="6"
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-600 resize-none"
                required
              ></textarea>
            </div>
            <div className="flex items-start mb-6">
              <div className="flex items-center h-5">
                <input
                  id="consent"
                  type="checkbox"
                  checked={isConsentChecked}
                  onChange={(e) => setIsConsentChecked(e.target.checked)}
                  className="w-4 h-4 border border-gray-300 rounded bg-gray-50 focus:ring-3 focus:ring-indigo-300"
                  required
                />
              </div>
              <label htmlFor="consent" className="ml-2 text-sm text-gray-600">
                En envoyant ce formulaire, vous consentez à ce que nous traitions vos données personnelles dans le respect de notre politique de confidentialité. 
                Nous nous engageons à protéger vos informations et à les utiliser uniquement pour répondre à votre demande. {' '}
                <a href="https://www.cnil.fr/" target="_blank" rel="noopener noreferrer" className="text-indigo-600">
                  Pour plus d'informations, consultez la CNIL.
                </a>
              </label>
            </div>
            <div className="flex justify-center">
              <button
                type="submit"
                disabled={isSubmitting || !isConsentChecked}
                className={`px-8 py-4 bg-indigo-600 text-white rounded-lg flex items-center ${isSubmitting || !isConsentChecked ? 'opacity-75 cursor-not-allowed' : 'hover:bg-indigo-700'}`}
              >
                {isSubmitting ? t('sending') : t('send')}
              </button>
            </div>
          </form>
          {status && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`mt-6 p-4 rounded-lg text-center ${
                status === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
              }`}
            >
              {status === 'success' ? (
                t('successMessage')
              ) : (
                <div>
                  <p>{t('errorMessage')}</p>
                  {errorDetails && (
                    <p className="text-sm mt-2 text-red-600">
                      Détails: {errorDetails}
                    </p>
                  )}
                </div>
              )}
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Contact;
